import React from 'react';
import { ArrowDown, ArrowUp, Check, UserPlus } from 'lucide-react';
import { SeverityBadge, StatusBadge } from '@/components/primitives';
import { briefId, cleanText, relTime } from '@/pages/inbox/inboxUtils';
import { formatUtcShort, ownerView } from './threatUtils';

export const COLUMNS = [
  { key: 'type', label: 'Type' },
  { key: 'actor', label: 'Actor', sort: 'actor' },
  { key: 'cves', label: 'CVEs' },
  { key: 'sources', label: 'Sources', sort: 'sources', num: true },
  { key: 'owner', label: 'Owner', sort: 'owner' },
  { key: 'due', label: 'Due', sort: 'due' },
  { key: 'status', label: 'Status', sort: 'status' },
];
export const DEFAULT_COLUMNS = ['actor', 'cves', 'owner', 'due', 'status'];
const TYPE = { attack: 'Attack', advisory: 'Advisory', informational: 'Info' };

function SortHeader({ label, sortKey, sort, onSort, className = '' }) {
  const on = sort.key === sortKey;
  return (
    <th className={className} aria-sort={on ? (sort.dir > 0 ? 'ascending' : 'descending') : undefined} scope="col">
      <button type="button" className="th-sort" onClick={() => onSort(sortKey)}>
        {label}
        {on && (sort.dir > 0 ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />)}
      </button>
    </th>
  );
}

/**
 * The threat queue as a table. One row is the cursor (j/k, click); ticking
 * rows selects them for bulk actions. Secondary columns step aside when the
 * detail panel is open, so the title keeps its room.
 */
export default function ThreatTable({
  rows, columns, sort, onSort, selectedId, onOpen, checked, onToggle, onToggleAll,
  getStatus, getStatusItem, getOwner, getSla, onQuickAck, onQuickAssign, panelOpen, me,
}) {
  const allChecked = rows.length > 0 && rows.every((b) => checked.has(briefId(b)));
  const has = (k) => columns.includes(k);
  const second = panelOpen ? 'th-col-2nd' : '';
  return (
    <div className="th-table-wrap">
      <table className="th-table" aria-label="Threat queue" aria-rowcount={rows.length}>
        <thead>
          <tr>
            <th className="th-cb" scope="col">
              <input type="checkbox" className="th-check" aria-label="Select every threat on this page" checked={allChecked} onChange={onToggleAll} />
            </th>
            <SortHeader label="Severity" sortKey="severity" sort={sort} onSort={onSort} />
            <SortHeader label="Threat" sortKey="name" sort={sort} onSort={onSort} className="th-title-col" />
            {has('type') && <th scope="col" className={`th-hide-sm ${second}`}>Type</th>}
            {has('actor') && <SortHeader label="Actor" sortKey="actor" sort={sort} onSort={onSort} className={`th-hide-sm ${second}`} />}
            {has('cves') && <th scope="col" className={`th-hide-sm ${second}`}>CVEs</th>}
            {has('sources') && <SortHeader label="Sources" sortKey="sources" sort={sort} onSort={onSort} className={`th-hide-sm th-num ${second}`} />}
            {has('owner') && <SortHeader label="Owner" sortKey="owner" sort={sort} onSort={onSort} className="th-hide-md" />}
            {has('due') && <SortHeader label="Due" sortKey="due" sort={sort} onSort={onSort} />}
            {has('status') && <SortHeader label="Status" sortKey="status" sort={sort} onSort={onSort} className={`th-hide-sm ${second}`} />}
            <SortHeader label="Age" sortKey="detected" sort={sort} onSort={onSort} className="th-num" />
            <th scope="col" className="th-acts th-hide-sm"><span className="sr-only">Quick actions</span></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => {
            const id = briefId(b);
            const status = getStatus(id);
            const owner = ownerView(getOwner(id), me);
            const sla = getSla(b, status, getStatusItem(id));
            const cves = (b.cves || []).filter((c) => /^CVE-/i.test(c));
            return (
              <tr
                key={id}
                id={`threat-row-${id}`}
                aria-selected={id === selectedId}
                className={id === selectedId ? 'is-cursor' : ''}
                onClick={() => onOpen(id)}
                data-testid="threat-row"
              >
                <td className="th-cb" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" className="th-check" aria-label={`Select ${b.name}`} checked={checked.has(id)} onChange={() => onToggle(id)} />
                </td>
                <td><SeverityBadge severity={b.severity} size="sm" /></td>
                <td className="th-title-col">
                  <button type="button" className="th-title" onClick={(e) => { e.stopPropagation(); onOpen(id, { focus: true }); }} title={cleanText(b.name)}>
                    {cleanText(b.name)}
                  </button>
                </td>
                {has('type') && <td className={`th-hide-sm th-muted ${second}`}>{TYPE[b.content_type || 'attack']}</td>}
                {has('actor') && <td className={`th-hide-sm th-actor ${second}`}>{b.threat_actor || <span className="th-muted">Unattributed</span>}</td>}
                {has('cves') && <td className={`th-hide-sm ${second}`}>{cves.length === 1 ? <span className="mono">{cves[0]}</span> : cves.length ? <span>{cves.length} CVEs</span> : <span className="th-muted">None</span>}</td>}
                {has('sources') && <td className={`th-hide-sm th-num ${second}`}>{b.source_count || 1}</td>}
                {has('owner') && <td className="th-hide-md">{owner ? <span className="th-owner"><b aria-hidden="true">{owner.initials}</b>{owner.label}</span> : <span className="th-muted">Unassigned</span>}</td>}
                {has('due') && <td className="th-due">{sla ? <span className={`th-sla th-sla--${sla.state}`} title={`Due ${formatUtcShort(sla.due)}`}>{sla.label}</span> : <span className="th-muted">No target</span>}</td>}
                {has('status') && <td className={`th-hide-sm ${second}`}><StatusBadge status={status} size="sm" /></td>}
                <td className="th-num th-muted" title={`Detected ${formatUtcShort(b.discovered_at)}`}>{relTime(b.discovered_at)}</td>
                <td className="th-acts th-hide-sm" onClick={(e) => e.stopPropagation()}>
                  <span className="th-quick">
                    {status !== 'acked' && (
                      <button type="button" className="sh-icon-btn" onClick={() => onQuickAck(b)} aria-label={`Acknowledge ${b.name}`} title="Acknowledge (A)"><Check className="sh-ic" aria-hidden="true" /></button>
                    )}
                    {!owner && (
                      <button type="button" className="sh-icon-btn" onClick={() => onQuickAssign(b)} aria-label={`Assign ${b.name} to me`} title="Assign to me (I)"><UserPlus className="sh-ic" aria-hidden="true" /></button>
                    )}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
